import type { WhatsAppClient } from '@adornis/whatsapp/server/messager.js';
import {
  getInstitutionWhatsappConfigRaw,
  type InstitutionWhatsappConfigData,
} from '../entities/institution-whatsapp-config.js';
import { whatsappDeploymentWhitelist } from './communication.js';

const WHATSAPP_CLIENT_TTL_MS = 60_000;
const whatsappClients = new Map<string, { client: WhatsAppClient; builtAt: number }>();

function hasCompleteCredentials(config: InstitutionWhatsappConfigData): boolean {
  return [config.appId, config.appSecret, config.businessId, config.phoneNumberId, config.accessToken].every(
    value => typeof value === 'string' && value.trim().length > 0,
  );
}

export async function isWhatsappConfiguredForInstitution(institutionID: string): Promise<boolean> {
  const config = await getInstitutionWhatsappConfigRaw(institutionID);
  return !!config && config.enabled === true && hasCompleteCredentials(config);
}

export async function getWhatsappClientForInstitution(institutionID: string): Promise<WhatsAppClient | null> {
  const cached = whatsappClients.get(institutionID);
  if (cached && Date.now() - cached.builtAt < WHATSAPP_CLIENT_TTL_MS) return cached.client;

  const config = await getInstitutionWhatsappConfigRaw(institutionID);
  if (!config || config.enabled !== true || !hasCompleteCredentials(config)) {
    whatsappClients.delete(institutionID);
    return null;
  }

  const { WhatsAppClient } = await import('@adornis/whatsapp/server/messager.js');
  const client = new WhatsAppClient(
    {
      appId: config.appId,
      appSecret: config.appSecret,
      bussinessId: config.businessId,
      accessToken: config.accessToken,
      phoneNumberId: config.phoneNumberId,
      version: config.apiVersion?.trim() || 'v20.0',
    },
    whatsappDeploymentWhitelist,
  );
  whatsappClients.set(institutionID, { client, builtAt: Date.now() });
  return client;
}
